import React, { useCallback, useEffect, useRef, useState } from "react";
import { Attention, Badge, Button, Typography } from "@optimizely/axiom";
import { useForm, useFormState } from "react-hook-form";
import { SidebarFooter } from "../Sidebar/SidebarFooter";
import { UserGroupWithInstance } from "../../../types/UserGroupWithInstance";
import { TeamUsers } from "../TeamUsers/TeamUsers";
import { User } from "../../../domain/User";
import { useTeams } from "../../../hooks/useTeams/useTeams";
import { emitToast } from "../../../lib/toaster-utils";
import { datadogRum } from "@datadog/browser-rum";
import { ERROR_KEYS } from "../../../constants/data-dog-constants";
import { ProductInstanceSelection } from "./ProductInstanceSelection/ProductInstanceSelection";
import { UserGroup } from "../../../domain/UserGroup";
import { useUserContext } from "../../../providers/UserProvider";
import {
    ATTRIBUTE_ROLES,
    GROUP_TYPES,
    PROPERTY_KEYS,
    REFETCH_PERMISSIONS_EVENT_NAME
} from "../../../constants/constants";
import { getCreateTeamPayload, getEditTeamPayload, getGrantPermissionsPayload } from "../../../lib/analytics-helpers";
import { ANALYTICS_FLOWS, ANALYTICS_TRACKED_COMPONENTS } from "../../../constants/analytics-constants";
import { TrackingFlow, useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { adaptApiErrors, IApiError } from "../../../services/ErrorMessageAdapter";
import classnames from "classnames";
import { useUsersByGroup } from "../../../hooks/useUsersByGroup/useUsersByGroup";

interface ITeamsFormValues {
    name: string;
    description?: string;
    usersToAdd: User[];
    usersToRemove: User[];
    productId: string;
    productInstanceId: string;
    apiError: { message: string };
}

export const TeamsForm = ({
    analyticsFlow = ANALYTICS_FLOWS.TEAMS,
    instanceId,
    loading: updatingTeam = false,
    onClose,
    team
}: {
    analyticsFlow?: TrackingFlow;
    instanceId?: string;
    loading?: boolean;
    onClose: () => void;
    team?: UserGroupWithInstance;
}) => {
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const { revalidate: refreshTeamUsers } = useUsersByGroup({ userGroupId: team?.id });

    const currentTeamId = useRef(team?.id);
    const { organizationId, canUserDoAction } = useUserContext();
    const isCreateMode = !team;
    const teamInstanceIds = instanceId ? [instanceId] : team?.instancePermissions?.map((ip) => ip.instanceId) ?? [];
    const hasInstanceScope = teamInstanceIds.length > 0;
    const canUpdateDetails =
        isCreateMode ||
        (hasInstanceScope
            ? teamInstanceIds.some((id) =>
                  canUserDoAction({ action: ATTRIBUTE_ROLES.GROUPS.UPDATE, context: { InstanceId: id } })
              )
            : canUserDoAction({ action: ATTRIBUTE_ROLES.GROUPS.UPDATE }));
    const canManageMembers =
        isCreateMode ||
        (hasInstanceScope
            ? teamInstanceIds.some((id) =>
                  canUserDoAction({ action: ATTRIBUTE_ROLES.GROUPS.MANAGE, context: { InstanceId: id } })
              )
            : canUserDoAction({ action: ATTRIBUTE_ROLES.GROUPS.MANAGE }));
    const { addTeam, updateTeam } = useTeams({ organizationId: organizationId ?? "" });
    const { sendTrackEvent } = useAnalyticsTracking();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        formState: { errors },
        setError,
        clearErrors,
        control
    } = useForm<ITeamsFormValues>({
        mode: "onChange",
        defaultValues: {
            name: team?.name ?? "",
            description: team?.description ?? "",
            usersToAdd: [],
            usersToRemove: [],
            productId: "",
            productInstanceId: ""
        }
    });
    const { isSubmitting } = useFormState({
        control
    });

    const watchedName = watch("name");
    const watchedProductInstanceId = watch("productInstanceId");

    // SCIM teams (internal groups) are read-only
    const isScimTeam = team?.groupType === GROUP_TYPES.INTERNAL;

    useEffect(() => {
        if (!team) {
            setEditing(true);
            return;
        }
        // SCIM teams should never be in edit mode
        if (isScimTeam && editing) {
            setEditing(false);
            return;
        }
        // handle switching the team being edited (users is already handled in TeamUsers)
        if (editing && currentTeamId.current !== team?.id) {
            setEditing(false);
            currentTeamId.current = team?.id ?? "";
            return;
        }
        if (team?.name !== getValues("name")) {
            setValue("name", team?.name ?? "");
        }
        if (team?.description !== getValues("description")) {
            setValue("description", team?.description ?? "");
        }
    }, [editing, getValues, setValue, team, isScimTeam]);

    const handleUserChanges = useCallback(
        ({ usersToAdd, usersToRemove }: { usersToAdd: User[]; usersToRemove: User[] }) => {
            setValue("usersToAdd", usersToAdd);
            setValue("usersToRemove", usersToRemove);
        },
        [setValue]
    );

    const handleProductInstanceSelection = useCallback(
        ({ productId, instanceId }: { productId: string; instanceId: string }) => {
            setValue("productId", productId);
            setValue("productInstanceId", instanceId);
        },
        [setValue]
    );

    const handleError = (apiErrors: IApiError[]) => {
        const errors = adaptApiErrors(apiErrors);

        errors.forEach((error) => {
            if (error?.field === "name") {
                setError("name", {
                    type: "individualFieldApiError",
                    message: error.message
                });
            } else {
                setError("apiError", {
                    type: "formApiError",
                    message: error.message || "Unable to create team at this time. Please contact your admin."
                });
            }
        });

        return errors;
    };

    const handleFormSubmission = async () => {
        if (!editing) {
            setEditing(true);
            return;
        }
        clearErrors("apiError");
        clearErrors("name");
        setLoading(true);
        const {
            description: newDescription,
            name: newName,
            usersToAdd,
            usersToRemove,
            productId,
            productInstanceId
        } = getValues();
        const updates: { [key: string]: any } = { usersToAdd, usersToRemove };
        if (canUpdateDetails && team?.name !== newName) {
            updates["name"] = newName;
        }
        if (canUpdateDetails && team?.description !== newDescription) {
            updates["description"] = newDescription;
        }

        if (!!team) {
            updateTeam({ team, ...updates })
                .then(() => {
                    const updateTeamAnalyticsPayload = getEditTeamPayload({
                        flow: analyticsFlow,
                        instanceId: team.instance?.id ?? "",
                        nameChanged: !!updates["name"],
                        descriptionChanged: !!updates["description"],
                        productId: team.instance?.productId ?? "",
                        usersAdded: usersToAdd?.map((u) => u.email) || [],
                        usersRemoved: usersToRemove?.map((u) => u.email) || []
                    });
                    sendTrackEvent(updateTeamAnalyticsPayload);
                    emitToast({ message: "Team updated successfully." });
                    refreshTeamUsers();
                    setEditing(false);
                })
                .catch((err) => {
                    const adaptedError = Array.isArray(err) ? handleError(err) : handleError([err]);
                    datadogRum.addError(new Error(ERROR_KEYS.TEAMS.FAILED_TO_UPDATE), {
                        teamId: team.id,
                        updates,
                        message: adaptedError[0]?.message,
                        source: "TeamsForm.handleFormSubmission"
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            const newTeam = new UserGroup({
                id: "0",
                name: newName,
                organizationId: organizationId!,
                description: newDescription || "",
                userCount: usersToAdd?.length || 0,
                groupType: GROUP_TYPES.CUSTOM,
                groupOwner: null,
                created: new Date(),
                modified: new Date(),
                instancePermissions: [
                    {
                        instanceId: productInstanceId,
                        roleIds: []
                    }
                ],
                users: usersToAdd?.map((u: User) => `${u.email}`),
                properties: [
                    {
                        name: PROPERTY_KEYS.TEAM,
                        value: "true"
                    }
                ]
            });
            addTeam(newTeam)
                .then((newlyCreatedTeam) => {
                    emitToast({ message: "Team successfully created." });
                    window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
                    const { id: groupId, name: teamName } = newlyCreatedTeam;
                    newlyCreatedTeam.users?.forEach((email) => {
                        const grantPermissionsPayload = getGrantPermissionsPayload({
                            component: ANALYTICS_TRACKED_COMPONENTS.TEAM_SIDEBAR,
                            flow: ANALYTICS_FLOWS.TEAMS,
                            email,
                            userGroupNames: [teamName],
                            userGroupIds: [groupId]
                        });
                        sendTrackEvent(grantPermissionsPayload);
                    });
                    const newTeamAnalyticsPayload = getCreateTeamPayload({
                        flow: analyticsFlow,
                        instanceId: productInstanceId,
                        productId,
                        teamName: newTeam.name,
                        users: newTeam.users
                    });
                    sendTrackEvent(newTeamAnalyticsPayload);
                    onClose();
                })
                .catch((error) => {
                    const adaptedError = Array.isArray(error) ? handleError(error) : handleError([error]);
                    datadogRum.addError(new Error(ERROR_KEYS.TEAMS.FAILED_TO_CREATE), {
                        message: adaptedError[0]?.message,
                        source: "TeamsForm.handleFormSubmission",
                        team: newTeam
                    });
                })
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        if (isSubmitting) {
            clearErrors(["apiError", "name"]);
        }
    }, [isSubmitting, clearErrors]);

    const isSaveDisabled = () => {
        if (loading || isSubmitting) return true;

        if (!watchedName?.trim()) return true;

        return !organizationId || (!team && !watchedProductInstanceId);
    };

    const formNameErrorClasses = classnames({
        "oui-form-bad-news": !!errors.name
    });

    return (
        <form onSubmit={handleSubmit(handleFormSubmission)}>
            <div className="push-quad--sides">
                {!!errors.apiError && (
                    <Attention className="push-double--bottom" alignment="left" type="bad-news">
                        {errors.apiError.message}
                    </Attention>
                )}
                {isScimTeam && (
                    <Attention className="push-double--bottom" alignment="left" type="good-news">
                        <Badge color="purple">SCIM</Badge> This team is managed by your SCIM provider and cannot be
                        edited or deleted.
                    </Attention>
                )}
                <label className="oui-label push-double--top" htmlFor="team-name">
                    Name
                    {editing && canUpdateDetails && <span aria-label="(required)" className="oui-label--required" />}
                </label>
                {editing && canUpdateDetails ? (
                    <div className={formNameErrorClasses}>
                        <input
                            aria-describedby="team-name"
                            className="oui-text-input"
                            placeholder="Team Name"
                            id="team-name"
                            data-cy="team-name-input"
                            disabled={loading}
                            type="text"
                            {...register("name", {
                                required: { value: true, message: "Team name is required." }
                            })}
                        />
                        {errors.name && (
                            <span className="oui-form-note" id="team-name-error">
                                {errors.name.message}
                            </span>
                        )}
                    </div>
                ) : (
                    <Typography type="body" className="label--disabled">
                        {team?.name}
                    </Typography>
                )}
                <label className="oui-label push-double--top" htmlFor="team-description">
                    Description
                </label>
                {editing && canUpdateDetails ? (
                    <textarea
                        aria-describedby="team-description"
                        className="oui-text-input"
                        placeholder="Team Description"
                        id="team-description"
                        data-cy="team-description-input"
                        disabled={loading}
                        {...register("description")}
                    />
                ) : (
                    <Typography type="body" className="label--disabled">
                        {team?.description && team?.description.trim() ? team?.description : "No description."}
                    </Typography>
                )}

                {!!team ? (
                    <>
                        <label className="oui-label push-double--top">Product</label>
                        <Typography type="body" className="label--disabled">
                            {team?.instance?.productName || "N/A"}
                        </Typography>

                        <label className="oui-label push-double--top">Instance</label>
                        <Typography type="body" className="label--disabled">
                            {team?.instance?.nickname || team?.instance?.name || "N/A"}
                        </Typography>
                    </>
                ) : (
                    <ProductInstanceSelection
                        instanceId={instanceId}
                        saving={loading}
                        onSelection={handleProductInstanceSelection}
                    />
                )}

                <label className="oui-label push-double--top">Users</label>
                <TeamUsers
                    disabled={loading}
                    loading={updatingTeam}
                    editing={editing && canManageMembers}
                    userGroupId={team?.id}
                    onChange={handleUserChanges}
                />
            </div>

            <SidebarFooter onCancel={onClose} cancelStyle="plain">
                {!isScimTeam && (canUpdateDetails || canManageMembers) ? (
                    <>
                        {!editing && !!team ? (
                            <Button
                                key="edit-team-button"
                                /* eslint-disable-next-line react/style-prop-object */
                                style="highlight"
                                onClick={() => setEditing(true)}
                                data-testid="edit-team-button"
                            >
                                Edit
                            </Button>
                        ) : (
                            <Button
                                key="save-team-button"
                                loadingText="Loading"
                                isDisabled={isSaveDisabled()}
                                style="highlight"
                                isSubmit
                                data-testid="save-team-button"
                            >
                                Save
                            </Button>
                        )}
                    </>
                ) : undefined}
            </SidebarFooter>
        </form>
    );
};

TeamsForm.displayName = "TeamsForm";
